import DrawModeBase from '@/lib/mapbox/draw-mode-base'
import { setCursor, resetCursor } from '@/services/map-service'
import Geographic from '@/lib/geographic'
import { getItem } from '@/plugins/storage'
import { isMobileTestbedActive } from '@/lib/prototyping-flags'
import { MARKUP_COORDINATES_CHANGED } from '@/lib/mapbox/drawing-events'

/**
 * Edit mode for editing existing waypoints.
 */
class WaypointEditMode extends DrawModeBase {
  /**
   * @inheritDoc
   */
  onSetup(waypointGeoJson) {
    const waypointId = waypointGeoJson.properties.id

    this.feature = waypointGeoJson
    this._state = {
      waypointId,
      dragMoveLocation: null,
      clickMoveLocation: null,
      geoPoint: null,
    }

    // set coordinate label
    const [lng, lat] = waypointGeoJson.geometry.coordinates
    this.feature.properties.coordString = this._getCoordString(lng, lat)
  }

  /**
   * @inheritDoc
   */
  onMouseDown(event) {
    const waypointId = this._state.waypointId
    const features = this.getFeaturesAtPoint(event.point)
    const isHoveringWaypoint = features.some(
      (feature) => feature.id === waypointId
    )

    if (isHoveringWaypoint) {
      this._startDrag(event)
    }
  }

  /**
   * @inheritDoc
   */
  onMouseUp() {
    if (this._isDragging()) {
      this._endDrag()
    }
  }

  /**
   * @inheritDoc
   */
  onMouseMove(event) {
    if (this._isDragging()) {
      this._handleDrag(event)
      return
    }

    const features = this.getFeaturesAtPoint(event.point)
    const waypointId = this._state.waypointId
    const isHoveringWaypoint = features.some(
      (feature) => feature.id === waypointId
    )

    if (isHoveringWaypoint) {
      setCursor('grab')
    } else {
      resetCursor()
    }
  }

  /**
   * @inheritDoc
   */
  onClick(event) {
    // only handle click event if mobile testbed prototyping is active
    // FUTURE: determine if we want this functionality on desktop as well, and if so, remove the flag
    if (isMobileTestbedActive()) {
      this._handleClick(event)
    }
  }

  /**
   * @inheritDoc
   */
  toDisplayFeatures(geoJson) {
    if (this._isDragging()) {
      geoJson.geometry.coordinates = this._state.dragMoveLocation
    } else if (this._state.clickMoveLocation) {
      geoJson.geometry.coordinates = this._state.clickMoveLocation
      this._state.clickMoveLocation = null
    }

    return [geoJson]
  }

  /**
   * Starts dragging the waypoint.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _startDrag(event) {
    this._state.dragMoveLocation = [event.lngLat.lng, event.lngLat.lat]
    this.map.dragPan.disable()
    setCursor('grabbing')
  }

  /**
   * Ends dragging the waypoint.
   * @private
   */
  _endDrag() {
    const waypointId = this._state.waypointId
    const coordinates = this._state.dragMoveLocation
    this._state.dragMoveLocation = null
    this.map.dragPan.enable()
    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id: waypointId,
      coordinates,
    })
    setCursor('grab')
  }

  /**
   * Whether the waypoint is currently being dragged.
   * @return {boolean} True if the waypoint is being dragged, otherwise false.
   * @private
   */
  _isDragging() {
    return this._state.dragMoveLocation !== null
  }

  /**
   * Handles dragging the drag waypoint event.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleDrag(event) {
    const { lng, lat } = event.lngLat
    this._state.dragMoveLocation = [lng, lat]

    // update coordinate label
    this.feature.properties.coordString = this._getCoordString(lng, lat)

    setCursor('grabbing')
    this.updated()
  }

  /**
   * Handles clicking map during waypoint edit.
   * @param {MapMouseEvent} event - docs.mapbox.com/mapbox-gl-js/api/events/#mapmouseevent
   * @private
   */
  _handleClick(event) {
    const { lng, lat } = event.lngLat
    this._state.clickMoveLocation = [lng, lat]

    // update coordinate label
    this.feature.properties.coordString = this._getCoordString(lng, lat)

    const waypointId = this._state.waypointId
    const coordinates = [lng, lat]
    this.events.emit(MARKUP_COORDINATES_CHANGED, {
      id: waypointId,
      coordinates,
    })
  }

  /**
   *
   * @param {string} lng - longitude string
   * @param {string} lat - latitude string
   * @returns
   */
  _getCoordString(lng, lat) {
    const coordinateType = getItem('coordinateType')
    this._state.geoPoint = new Geographic(lng, lat)
    return this._state.geoPoint.getCoordinatesStringByType(coordinateType)
  }
}

export default WaypointEditMode
